import _get from 'lodash/get'
import { z } from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'

import { underwritingStep } from '@flows/configs/dental/utils/navigation'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import { previousInsuranceOptions } from './questions'

export const amountRow = (params: StepConfigParams, labelKey: string, amountKey: string) =>
  ({
    label: { type: 'translatableText', translationKey: labelKey },
    value: '',
    textValue: {
      type: 'nonTranslatableText',
      text: '{{amount}}',
      values: {
        amount: {
          type: 'dynamicValue',
          path: params.helperFunctions.getTransientPath(amountKey),
        },
      },
    },
  }) as const

export const unlimitedRow = {
  label: {
    type: 'translatableText',
    translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.limits.afterwards',
  },
  value: '',
  textValue: {
    type: 'translatableText',
    translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.limits.unlimited',
  },
} as const

export const claimSublimitRows = (
  params: StepConfigParams,
  {
    firstYearKey = 'claim_sublimit_for_first_year_standard',
    boldUnlimited = true,
  }: { firstYearKey?: string; boldUnlimited?: boolean } = {}
) =>
  [
    {
      ...amountRow(
        params,
        'sharedFlows_dental:steps.dental_previous_insurance.info.limits.firstYear',
        firstYearKey
      ),
      config: {
        boldValue: true,
      },
    },
    {
      ...amountRow(
        params,
        'sharedFlows_dental:steps.dental_previous_insurance.info.limits.firstTwoYears',
        'claim_sublimit_for_second_year'
      ),
      config: {
        boldValue: true,
      },
    },
    {
      ...amountRow(
        params,
        'sharedFlows_dental:steps.dental_previous_insurance.info.limits.firstThreeYears',
        'claim_sublimit_for_third_year'
      ),
      config: {
        boldValue: true,
      },
    },
    {
      ...unlimitedRow,
      ...(boldUnlimited && { config: { boldValue: true } }),
    },
  ] as const

const dental_previous_insurance = (params: StepConfigParams, plaform: 'native' | 'web') => {
  const nextStep = underwritingStep(plaform)

  return {
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('previous_insurance')]: z.enum([
        'unknown',
        'true',
        'false',
      ]),
    }),
    onPrepare: {
      type: 'simpleHook',
      callback: async () => {
        const response = await params.clientHelpers.apiService.call({
          serviceName: 'insurance',
          method: HTTP_METHOD.POST,
          url: `/api/v2/markets/${params.clientHelpers.getMarket()}/dental/claim_sublimits`,
          payload: {
            previous_insurance: 'unknown',
          },
        })

        return {
          formValues: {
            [params.helperFunctions.getTransientPath('claim_sublimit_for_first_year_standard')]:
              params.clientHelpers.formatter.formatCurrency(
                Number(response?.data?.claim_sublimit_for_first_year)
              ),
            [params.helperFunctions.getTransientPath('claim_sublimit_for_second_year')]:
              params.clientHelpers.formatter.formatCurrency(
                Number(response?.data?.claim_sublimit_for_second_year)
              ),
            [params.helperFunctions.getTransientPath('claim_sublimit_for_third_year')]:
              params.clientHelpers.formatter.formatCurrency(
                Number(response?.data?.claim_sublimit_for_third_year)
              ),
          },
        }
      },
    },
    showOrderSummary: true,
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.title',
        },
        size: 'h3',
      },
      {
        type: 'choice',
        variant: 'filled',
        shouldTrackValues: true,
        name: params.helperFunctions.getParamPath('previous_insurance'),
        options: previousInsuranceOptions,
      },
      {
        type: 'infoBox',
        noPadding: true,
        bgColor: 'grey200',
        contentConfiguration: [
          {
            type: 'title',
            size: 'h5',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.title',
            },
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'semibold',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.text1',
            },
          },
          {
            type: 'spacer',
            size: 'small',
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'regular',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.text2',
            },
          },
          {
            type: 'spacer',
            size: 'small',
          },
          {
            type: 'text',
            style: 'caption',
            color: 'black',
            weight: 'semibold',
            emphasis: 'default',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.text3',
            },
          },
          {
            type: 'spacer',
            size: 'small',
          },
          {
            type: 'title',
            size: 'h5',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.info.limitsTitle',
            },
          },
          {
            type: 'list',
            isBoldLabel: true,
            padding: 'none',
            paddingLeft: 'none',
            data: claimSublimitRows(params),
          },
        ],
      },
    ],
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valuePath: params.helperFunctions.getParamPath('previous_insurance'),
      valueToStepName: {
        true: 'dental_previous_insurer',
        false: nextStep,
      },
    },
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => {
        const hasPrevious = _get(
          flowValues,
          params.helperFunctions.getParamPath('previous_insurance')
        )

        if (hasPrevious === 'false') {
          return {
            formValues: {
              [params.helperFunctions.getParamPath('previous_insurer')]: undefined,
              [params.helperFunctions.getParamPath('previous_insurance_start_date')]: undefined,
              [params.helperFunctions.getParamPath('previous_insurance_end_date')]: undefined,
              [params.helperFunctions.getParamPath(
                'previous_insurance_covers_min_75_percent_dental_replacements'
              )]: undefined,
            },
          }
        }
      },
    },
  } as const satisfies AnyStepConfiguration
}

export default dental_previous_insurance
